import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TablePagination from "@mui/material/TablePagination";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import XLSX from "xlsx-js-style";
import moment from "moment";
import toast from "react-hot-toast";
import "moment/locale/pt-br";
moment.locale("pt-br");

const statusColor = (status) => {
  switch (status) {
    case "completo":
      return "success";
    case "parcial":
      return "warning";
    case "falhou":
      return "error";
    default:
      return "default";
  }
};

const statusLabel = (status) => {
  switch (status) {
    case "completo":
      return "Completo";
    case "parcial":
      return "Parcial";
    case "falhou":
      return "Falhou";
    default:
      return status || "-";
  }
};

function RelatorioEmail({
  dataFiltro,
  clinicaId,
  auth,
  acao,
  sendRequest,
  atualizacoes,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [logs, setLogs] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);
  const [error, setError] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(10);

  useEffect(() => {
    if (acao === "gerar") gerarRelatorio();
    else if (acao === "exportar") exportarRelatorio();
  }, [acao, atualizacoes]);

  const gerarRelatorio = async () => {
    setError(null);
    setLogs(null);
    setEstatisticas(null);
    startLoading();
    try {
      const headers = { Authorization: "Bearer " + auth.token };
      const dataInicio = dataFiltro.dataInicial.format("YYYY-MM-DD");
      const dataFim = dataFiltro.dataFinal.format("YYYY-MM-DD");
      const query = `?dataInicio=${dataInicio}&dataFim=${dataFim}`;

      const [resHistorico, resEstatisticas] = await Promise.all([
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/email/historico/${clinicaId}${query}`,
          "GET",
          null,
          headers,
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/email/estatisticas/${clinicaId}${query}`,
          "GET",
          null,
          headers,
        ),
      ]);

      setLogs(resHistorico.logs);
      setEstatisticas(resEstatisticas);
    } catch (err) {
      setError(err.message || "Erro ao carregar relatório de emails");
    } finally {
      stopLoading();
    }
  };

  const exportarRelatorio = () => {
    if (!logs) {
      toast.error("É necessário gerar o relatório primeiro.");
      return;
    }

    const wb = XLSX.utils.book_new();

    // Aba resumo
    const wsResumo = XLSX.utils.aoa_to_sheet([
      ["Relatório de Emails"],
      [
        `Período: ${dataFiltro.dataInicial.format("DD/MM/YYYY")} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
      ],
      [],
      ["Total de Envios", "Total Destinatários", "Entregues", "Falhados"],
      [
        estatisticas?.totalEmails ?? "-",
        estatisticas?.totalDestinatarios ?? "-",
        estatisticas?.totalEnviados ?? "-",
        estatisticas?.totalFalhados ?? "-",
      ],
    ]);
    wb.SheetNames.push("Resumo");
    wb.Sheets["Resumo"] = wsResumo;

    // Aba histórico
    const wsHistorico = XLSX.utils.aoa_to_sheet([
      [
        "Assunto",
        "Status",
        "Enviado por",
        "Destinatários",
        "Entregues",
        "Falhados",
        "Tem Anexos",
        "Data",
      ],
      ...logs.map((l) => [
        l.assunto || "-",
        statusLabel(l.status),
        l.enviadoPor?.name || "-",
        l.totalDestinatarios,
        l.totalEnviados,
        l.totalFalhados,
        l.temAnexos ? "Sim" : "Não",
        moment(l.created_at).format("DD/MM/YYYY HH:mm"),
      ]),
    ]);
    wb.SheetNames.push("Histórico");
    wb.Sheets["Histórico"] = wsHistorico;

    XLSX.writeFile(
      wb,
      `relatorio_emails_${moment().format("YYYY-MM-DD")}.xlsx`,
    );
    toast.success("Arquivo exportado com sucesso!");
  };

  const logsPaginados = logs
    ? logs.slice(
        paginaAtual * registrosPorPagina,
        paginaAtual * registrosPorPagina + registrosPorPagina,
      )
    : [];

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
          Relatório de Emails
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Cartões de estatísticas */}
        {estatisticas && (
          <Box sx={{ display: "flex", gap: 3, my: 3, flexWrap: "wrap" }}>
            {[
              {
                label: "Total de Envios",
                value: estatisticas.totalEmails,
                color: "#1976d2",
              },
              {
                label: "Total Destinatários",
                value: estatisticas.totalDestinatarios,
                color: "#7b1fa2",
              },
              {
                label: "Entregues",
                value: estatisticas.totalEnviados,
                color: "#4caf50",
              },
              {
                label: "Falhados",
                value: estatisticas.totalFalhados,
                color: "#f44336",
              },
            ].map((card) => (
              <Paper
                key={card.label}
                elevation={2}
                sx={{
                  p: 2,
                  minWidth: 160,
                  borderLeft: `4px solid ${card.color}`,
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: card.color }}
                >
                  {card.value}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Tabela */}
        {logs && (
          <>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Assunto</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Enviado por</TableCell>
                    <TableCell>Destinatários</TableCell>
                    <TableCell>Entregues</TableCell>
                    <TableCell>Falhados</TableCell>
                    <TableCell>Anexos</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Detalhes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsPaginados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3 }}
                        >
                          Nenhum email encontrado no período selecionado.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logsPaginados.map((log) => (
                      <TableRow key={log._id}>
                        <TableCell>{log.assunto || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel(log.status)}
                            color={statusColor(log.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.enviadoPor?.name || "-"}</TableCell>
                        <TableCell>{log.totalDestinatarios}</TableCell>
                        <TableCell>{log.totalEnviados}</TableCell>
                        <TableCell>{log.totalFalhados}</TableCell>
                        <TableCell>{log.temAnexos ? "Sim" : "Não"}</TableCell>
                        <TableCell>
                          {moment(log.created_at).format("DD/MM/YYYY HH:mm")}
                        </TableCell>
                        <TableCell>
                          {log.destinatarios?.length > 0 && (
                            <Accordion
                              disableGutters
                              elevation={0}
                              sx={{ background: "transparent" }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ p: 0, minHeight: 0 }}
                              >
                                <Typography variant="caption">
                                  Ver destinatários
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 0 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Nome</TableCell>
                                      <TableCell>Email</TableCell>
                                      <TableCell>Sucesso</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {log.destinatarios.map((d, i) => (
                                      <TableRow key={i}>
                                        <TableCell>{d.nome || "-"}</TableCell>
                                        <TableCell>{d.email}</TableCell>
                                        <TableCell>
                                          <Chip
                                            label={d.sucesso ? "Sim" : "Não"}
                                            color={
                                              d.sucesso ? "success" : "error"
                                            }
                                            size="small"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={logs.length}
              page={paginaAtual}
              onPageChange={(e, novaPagina) => setPaginaAtual(novaPagina)}
              rowsPerPage={registrosPorPagina}
              onRowsPerPageChange={(e) => {
                setRegistrosPorPagina(parseInt(e.target.value, 10));
                setPaginaAtual(0);
              }}
              labelRowsPerPage="Registros por página"
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default RelatorioEmail;
